import PropTypes from "prop-types"
import React, { useEffect, useRef, useState } from "react"
import { Row, Col, Card, CardBody, Label } from "reactstrap"
import { Formik, Form, Field } from "formik"

import { useDispatch } from "react-redux"
import { Link } from "react-router-dom"
import withRouter from "components/Common/withRouter"

import { verifyOTPSchema } from "components/Common/validation"
import { resendLoginOtp, verifyLoginOtp } from "store/actions/authAction"
import { showAlert } from "store/reducers/alertReducer"
import { useTranslation } from "react-i18next"
import TextError from "components/Shared/TextError"
import logo from "assets/images/logo.svg"

const OTP_RESEND_COOLDOWN_SECONDS = 60

const getRemainingCooldown = sentAt => {
  if (!sentAt) return 0
  const elapsedSeconds = Math.floor((Date.now() - Number(sentAt)) / 1000)
  return Math.max(0, OTP_RESEND_COOLDOWN_SECONDS - elapsedSeconds)
}

const formatRemaining = (seconds, t) => {
  if (!seconds || seconds <= 0) return ""
  if (seconds < 60) {
    return seconds === 1 ? t("second_singular") : t("second_plural", { count: seconds })
  }
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  const minsStr = mins === 1 ? t("minute_singular") : t("minute_plural", { count: mins })
  if (secs === 0) return minsStr
  const secsStr = secs === 1 ? t("second_singular") : t("second_plural", { count: secs })
  return `${minsStr} ${secsStr}`
}

const LoginVerifyOtp = props => {
  const { t } = useTranslation()
  document.title = t("Verify OTP | Sign Burger")

  const dispatch = useDispatch()
  const formikRef = useRef(null)
  const [verifying, setVerifying] = useState(false)
  const [resending, setResending] = useState(false)
  const [cooldownSeconds, setCooldownSeconds] = useState(0)

  useEffect(() => {
    if (cooldownSeconds <= 0) return undefined
    const interval = setInterval(() => {
      setCooldownSeconds(prev => (prev <= 1 ? 0 : prev - 1))
    }, 1000)
    return () => clearInterval(interval)
  }, [cooldownSeconds])

  useEffect(() => {
    const sentAt = Number(sessionStorage.getItem("loginOtpSentAt") || 0)
    setCooldownSeconds(getRemainingCooldown(sentAt))
  }, [])

  const handleOtpVerify = async (values, { setSubmitting }) => {
    const loginEmail = sessionStorage.getItem("loginEmail")
    const payload = {
      email: loginEmail,
      otp: values.otp,
    }
    setVerifying(true)
    try {
      const action = await dispatch(
        verifyLoginOtp({ ...payload, history: props.router.navigate })
      )
      const { statusCode, status, msg, errorCode, data } = (action && action.payload) || {}
      if (statusCode === 200 && status) {
        dispatch(showAlert({ text: msg, type: "success" }))
      } else {
        let displayMsg = msg
        if (errorCode === "ACCOUNT_LOCKED" && data && data.remainingSeconds) {
          displayMsg = t("Too many failed login attempts. Your account is temporarily locked. Please try again after {{time}}.", {
            time: formatRemaining(data.remainingSeconds, t),
          })
          props.router.navigate("/login")
        } else if (errorCode === "INCORRECT_OTP" && data && data.remainingAttempts !== undefined) {
          displayMsg = t("{{msg}} {{remaining}} attempt(s) remaining.", {
            msg,
            remaining: data.remainingAttempts,
          })
        } else if (
          (errorCode === "OTP_RESEND_COOLDOWN" || errorCode === "RATE_LIMITED" || errorCode === "OTP_SEND_LIMITED") &&
          (data && data.remainingSeconds)
        ) {
          displayMsg = t("Too many OTP requests. Please try again after {{time}}.", {
            time: formatRemaining(data.remainingSeconds, t),
          })
        }
        dispatch(showAlert({ text: displayMsg, type: "error" }))
        // The verify-OTP endpoint has its own rate limiter; once it trips,
        // the OTP is effectively unusable until the window resets. Reuse the
        // resend cooldown UI to reflect that instead of leaving the button
        // clickable for another guaranteed 429.
        if (errorCode === "RATE_LIMITED" && (data && data.remainingSeconds)) {
          setCooldownSeconds(data.remainingSeconds)
        }
      }
    } finally {
      setVerifying(false)
      setSubmitting(false)
    }
  }

  const handleResendOtp = () => {
    if (cooldownSeconds > 0) return
    const loginEmail = sessionStorage.getItem("loginEmail")
    if (!loginEmail) {
      props.router.navigate("/login")
      return
    }
    setResending(true)
    dispatch(resendLoginOtp({ email: loginEmail }))
      .then(action => {
        const { statusCode, status, msg, errorCode, data } = (action && action.payload) || {}
        if (statusCode === 200 && status) {
          if (formikRef.current && formikRef.current.setFieldValue) formikRef.current.setFieldValue("otp", "")
          sessionStorage.setItem("loginOtpSentAt", Date.now())
          dispatch(showAlert({ text: msg, type: "success" }))
          setCooldownSeconds(OTP_RESEND_COOLDOWN_SECONDS)
        } else {
          let displayMsg = msg
          if (errorCode === "ACCOUNT_LOCKED" && data && data.remainingSeconds) {
            displayMsg = t("Too many failed login attempts. Your account is temporarily locked. Please try again after {{time}}.", {
              time: formatRemaining(data.remainingSeconds, t),
            })
            props.router.navigate("/login")
          } else if (
            (errorCode === "OTP_RESEND_COOLDOWN" ||
              errorCode === "RATE_LIMITED" ||
              errorCode === "OTP_SEND_LIMITED") &&
            data &&
            data.remainingSeconds
          ) {
            displayMsg = t("Too many OTP resend requests. Please try again after {{time}}.", {
              time: formatRemaining(data.remainingSeconds, t),
            })
            setCooldownSeconds(data.remainingSeconds)
          }
          dispatch(showAlert({ text: displayMsg, type: "error" }))
        }
      })
      .finally(() => setResending(false))
  }

  return (
    <React.Fragment>
      <div className="account-pages">
        <div className="login_form">
          <Card className="overflow-hidden">
            <div className="bg-primary-subtle">
              <Row>
                <Col xs={12}>
                  <div className="text-primary p-4">
                    <h5 className="text-primary">{t("Verify OTP")}</h5>
                    <p>
                      {t(
                        "Enter the OTP sent to your registered email to complete sign in."
                      )}
                    </p>
                  </div>
                </Col>
              </Row>
            </div>
            <CardBody className="pt-0">
              <div>
                <Link to="/">
                  <div className="avatar-md profile-user-wid mb-4">
                    <span className="avatar-title rounded-circle bg-light">
                      <img
                        src={logo}
                        alt=""
                        className="rounded-circle"
                        height="34"
                      />
                    </span>
                  </div>
                </Link>
              </div>
              <div className="p-2">
                <Formik
                  innerRef={formikRef}
                  enableReinitialize={true}
                  initialValues={{ otp: "" }}
                  validationSchema={verifyOTPSchema}
                  onSubmit={handleOtpVerify}
                >
                  {({ touched, errors }) => (
                    <Form className="form-horizontal">
                      <div className="mb-3">
                        <Label className="form-label">{t("Verify OTP")}</Label>
                        <Field
                          name="otp"
                          className={`form-control ${
                            touched.otp && errors.otp && "is-invalid"
                          }`}
                          placeholder={t("Enter OTP")}
                          type="text"
                        />
                        <TextError name="otp" />
                      </div>
                      <Row className="mb-3">
                        <Col className="text-end">
                          <button
                            className="btn btn-primary w-md"
                            type="submit"
                            disabled={verifying}
                          >
                            {t("Verify")}
                          </button>
                        </Col>
                      </Row>
                      <div className="text-center mt-2">
                        <button
                          type="button"
                          className="btn btn-link text-primary p-0"
                          onClick={handleResendOtp}
                          disabled={resending || cooldownSeconds > 0}
                        >
                          {cooldownSeconds > 0
                            ? t("Resend OTP in {{time}}", {
                                time: formatRemaining(cooldownSeconds, t),
                              })
                            : t("Resend OTP")}
                        </button>
                      </div>
                    </Form>
                  )}
                </Formik>
              </div>
            </CardBody>
          </Card>
          <div className="text-center auth-page-footer">
            <p className="mb-0">
              {t("Go back to")}{" "}
              <Link to="/login" className="font-weight-medium text-black">
                {t("Login")}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </React.Fragment>
  )
}

LoginVerifyOtp.propTypes = {
  history: PropTypes.object,
}

export default withRouter(LoginVerifyOtp)
